import React, { useEffect, useMemo, useState } from "react";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Search, UserCog } from "lucide-react";

const ROLES = ["all", "brand", "creator", "admin", "unset"];

const roleBadge = (role) => {
    const map = {
        brand: "bg-yc-blue text-white",
        creator: "bg-yc-pink text-white",
        admin: "bg-yc-ink text-white",
        unset: "bg-yc-cream text-yc-ink/70",
    };
    return map[role] || map.unset;
};

const AdminUsers = () => {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");
    const [busyId, setBusyId] = useState("");

    const load = async () => {
        try {
            const r = await api.get("/admin/users");
            setUsers(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not load users");
        }
    };
    useEffect(() => { load(); }, []);

    const shown = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return users.filter((u) => {
            if (filter !== "all" && (u.role || "unset") !== filter) return false;
            if (!needle) return true;
            return (u.email || "").toLowerCase().includes(needle) || (u.name || "").toLowerCase().includes(needle);
        });
    }, [users, q, filter]);

    const counts = useMemo(() => {
        const c = { all: users.length, brand: 0, creator: 0, admin: 0, unset: 0 };
        users.forEach((u) => { c[u.role || "unset"] = (c[u.role || "unset"] || 0) + 1; });
        return c;
    }, [users]);

    const changeRole = async (u, role) => {
        if (role === (u.role || "unset")) return;
        const label = role === "unset" ? "reset (they'll re-pick on next visit)" : role;
        if (!window.confirm(`Change ${u.email} to ${label}?`)) return;
        setBusyId(u.user_id);
        try {
            await api.post(`/admin/users/${u.user_id}/role`, { role });
            toast.success(`${u.email} is now ${role === "unset" ? "role-less — they'll choose again on next login" : `a ${role}`}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not change role");
        } finally { setBusyId(""); }
    };

    return (
        <DashboardShell role="admin" title="Users" subtitle="Every account on the platform — change any email's access here">
            <div className="yc-card p-6">
                <div className="flex items-center gap-2">
                    <UserCog className="w-5 h-5 text-yc-blue" />
                    <h3 className="font-heading text-xl font-bold">Accounts ({users.length})</h3>
                </div>
                <p className="text-sm text-yc-ink/70 mt-1">
                    A Google account locks to the role it first picked. Use this list to switch someone between brand and creator,
                    or reset them so they choose again on their next login.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                    <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-yc-ink/40" />
                        <input
                            data-testid="users-search-input"
                            className="yc-input w-full pl-9"
                            placeholder="Search by email or name…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        {ROLES.map((r) => (
                            <button
                                key={r}
                                data-testid={`users-filter-${r}`}
                                onClick={() => setFilter(r)}
                                className={`text-xs font-bold px-3 py-2 rounded-full border-2 border-yc-ink transition-colors ${filter === r ? "bg-yc-ink text-white" : "bg-white hover:bg-yc-cream"}`}
                            >
                                {r === "all" ? "All" : r} ({counts[r] ?? 0})
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mt-4 space-y-3">
                    {shown.map((u) => (
                        <div key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-2 border-yc-ink rounded-2xl p-4 bg-white flex flex-wrap items-center gap-3">
                            <div className="flex-1 min-w-[220px]">
                                <div className="font-heading font-bold">{u.name || "—"}</div>
                                <div className="text-xs text-yc-ink/60" data-testid={`user-email-${u.user_id}`}>{u.email}</div>
                                <div className="text-xs text-yc-ink/50">
                                    Joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                                    {u.has_brand_profile && " · has brand profile"}
                                    {u.has_creator_profile && " · has creator profile"}
                                </div>
                            </div>
                            <span data-testid={`user-role-badge-${u.user_id}`} className={`yc-badge text-[10px] ${roleBadge(u.role || "unset")}`}>
                                {u.role || "unset"}
                            </span>
                            {u.user_id === me?.user_id ? (
                                <span className="text-xs text-yc-ink/50 font-semibold">you</span>
                            ) : (
                                <select
                                    data-testid={`user-role-select-${u.user_id}`}
                                    className="yc-input text-xs py-2 w-auto"
                                    value={u.role || "unset"}
                                    disabled={busyId === u.user_id}
                                    onChange={(e) => changeRole(u, e.target.value)}
                                >
                                    <option value="brand">brand</option>
                                    <option value="creator">creator</option>
                                    <option value="admin">admin</option>
                                    <option value="unset">reset — re-pick</option>
                                </select>
                            )}
                        </div>
                    ))}
                    {shown.length === 0 && <div className="text-center text-yc-ink/50 text-sm py-6">No accounts match.</div>}
                </div>
            </div>
        </DashboardShell>
    );
};

export default AdminUsers;
